import { ApiError } from "./errors";

/**
 * SEC-M3 (audit-2026-05-19#m3): a previous version of this module stored a
 * fallback API key in sessionStorage. The slot was JS-readable from any
 * script that executed in the page (transitive frontend deps included),
 * which would have leaked the key to any XSS sink. The fallback was also
 * dead code — no UI surface ever populated the storage and no caller
 * imported setApiKey() — so the surface has been removed entirely.
 *
 * If we ever need a non-Firebase auth path again, the right pattern is the
 * existing session cookie (HttpOnly + CSRF), not JS-accessible storage.
 */

type TokenGetter = () => Promise<string | null>;

let tokenGetter: TokenGetter | null = null;
let tokenGetterReady = false;

/* c8 ignore start */
export const DEBUG_AUTH = import.meta.env.DEV ||
  import.meta.env.VITE_DEBUG_AUTH === "true";

function debugLog(message: string, ..._args: unknown[]) {
  if (DEBUG_AUTH) {
    console.debug(`API client: ${message}`);
  }
}
/* c8 ignore stop */

export function setTokenGetter(getter: TokenGetter | null) {
  debugLog("setTokenGetter called", { hasGetter: !!getter });
  tokenGetter = getter;
  tokenGetterReady = !!getter;
}

export function isTokenGetterReady(): boolean {
  return tokenGetterReady;
}

export async function getAuthToken(): Promise<string> {
  debugLog("getAuthToken called", { hasTokenGetter: !!tokenGetter });

  if (!tokenGetterReady) {
    console.warn(
      "API client: token getter not initialized; auth system may not be ready",
    );
  }

  if (tokenGetter) {
    try {
      const token = await tokenGetter();
      if (token) {
        debugLog("Firebase token retrieved successfully", {
          tokenLength: token.length,
        });
        return token;
      }
      debugLog("tokenGetter returned null");
    } catch (error) {
      debugLog("tokenGetter threw error", { error });
    }
  }

  console.warn("API client: no authentication available");
  throw new ApiError(
    "authentication_required",
    401,
    undefined,
    "AUTH_NO_TOKEN",
  );
}

export async function getHeaders(
  includeContentType = false,
): Promise<HeadersInit> {
  const token = await getAuthToken();
  const headers: HeadersInit = {
    "Authorization": `Bearer ${token}`,
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
}
